import React, { memo } from "react";
import { Box, Flex, Text, Tooltip } from "@chakra-ui/react";
import { PaperAirplaneIcon } from "@heroicons/react/24/outline";
import { ArrowLeftCircleIcon } from "@heroicons/react/24/solid";
import { a } from "../appColors";

/**
 * The bar across the top of a paper (Figma 193:55).
 *
 * Three zones on one line: a way back, what you are sitting, and the way out.
 * The title is centred and the two controls are pinned to the edges, so the
 * paper name stays in the middle whatever the controls are doing — the old
 * header left-aligned everything and the title drifted as buttons appeared.
 *
 * It renders what it is told and calls back; the timer, the proctoring and
 * the mode switching stay with whoever owns them.
 */
const SolveHeaderBar = ({
  subjectName,
  paperName,
  duration,
  onBack,
  backDisabled = false,
  onSubmit,
  submitLabel = "Submit",
  canSubmit = true,
  isSubmitting = false,
  children,
}) => (
  <Flex
    as="header"
    align="center"
    justify="space-between"
    gap="16px"
    px={{ base: "14px", md: "28px" }}
    py="6px"
    minH="52px"
    flexShrink={0}
    // No card of its own: the bar sits straight on the canvas, so the page
    // reads as one surface instead of a strip laid over another strip.
    bg="transparent"
    width="100%"
  >
    <Flex
      as="button"
      type="button"
      onClick={backDisabled ? undefined : onBack}
      aria-label="Back"
      align="center"
      gap="10px"
      fontSize="14px"
      color={a.text}
      cursor={backDisabled ? "not-allowed" : "pointer"}
      opacity={backDisabled ? 0.45 : 1}
      flexShrink={0}
      _hover={backDisabled ? {} : { color: a.accent }}
      _focusVisible={{ outline: "2px solid", outlineColor: a.text, outlineOffset: "3px" }}
    >
      <ArrowLeftCircleIcon style={{ width: 26, height: 26, flexShrink: 0 }} />
      <Text as="span" display={{ base: "none", sm: "inline" }}>
        Back
      </Text>
    </Flex>

    {/* min-width 0 so a long paper name truncates instead of shoving the
        submit button off the edge. */}
    <Flex direction="row" align="center" justify="center" gap="10px" minW={0} flex="1" flexWrap="wrap">
      <Text fontSize={{ base: "15px", md: "18px" }} fontWeight="500" color={a.text} noOfLines={1}>
        {subjectName}
      </Text>
      <Flex
        align="center"
        justify="center"
        gap="7px"
        px="12px"
        py="2px"
        borderRadius="200px"
        bg={a.surface}
        border="1px solid"
        borderColor={a.edge}
        maxW="100%"
      >
        <Text fontSize={{ base: "12px", md: "13px" }} color={a.text} noOfLines={1}>
          {paperName}
        </Text>
        {duration ? (
          <>
            <Box boxSize="4px" borderRadius="full" bg={a.blue} flexShrink={0} />
            <Text
              fontSize={{ base: "12px", md: "13px" }}
              fontWeight="300"
              fontStyle="italic"
              color={a.text}
              whiteSpace="nowrap"
            >
              {duration}
            </Text>
          </>
        ) : null}
      </Flex>
    </Flex>

    <Flex align="center" gap="10px" flexShrink={0}>
      {/* Where a mode puts its own controls — test mode's timer, review's
          comment and report buttons — without this component knowing them. */}
      {children}

      {onSubmit && (
        <Tooltip label="Submit the whole paper" placement="bottom" hasArrow fontSize="11px">
          <Flex
            as="button"
            type="button"
            align="center"
            justify="center"
            gap="6px"
            w="104px"
            p="8px"
            borderRadius="200px"
            bg={a.surfaceSubtle}
            border="1px solid"
            borderColor={a.text}
            color={a.text}
            fontSize="12px"
            fontWeight="500"
            opacity={canSubmit && !isSubmitting ? 1 : 0.5}
            cursor={canSubmit && !isSubmitting ? "pointer" : "not-allowed"}
            transition="background 0.12s, color 0.12s"
            _hover={canSubmit && !isSubmitting ? { bg: a.text, color: a.surface } : {}}
            onClick={canSubmit && !isSubmitting ? onSubmit : undefined}
          >
            <Text as="span">{isSubmitting ? "Submitting" : submitLabel}</Text>
            <PaperAirplaneIcon style={{ width: 18, height: 18 }} />
          </Flex>
        </Tooltip>
      )}
    </Flex>
  </Flex>
);

export default memo(SolveHeaderBar);
